/**
 * useStudentGenerateActivity — « Générer une activité » pour l'élève lui-même : le parcours du
 * parent (`useGenerateActivityFlow`, lu tel quel), à la voix de l'élève, avec ce que le legacy
 * `selfCreate` en retire et ce qu'il y ajoute (`GenerateActivityModal.js`, monté par
 * `TodoListScreen.js:1257-1265` avec `student={props.user}` et `selfCreate`).
 *
 * Les appels sont ceux du parent, avec l'identifiant de l'élève dans l'URL — vérifiés ouverts à
 * l'élève pour lui-même :
 * - `getChaptersAsTutor` → `GET v1/students/{student}/chapters`, `can:showStudent,student` : la
 *   première branche de `UsersPolicy::showStudent` est `$user->id === $student->id` (le legacy
 *   `selfCreate` appelle déjà cette route avec `student.id`) ;
 * - `createActivityAsTutor` → `POST v2/students/{student}/activities/todo`,
 *   `can:manageActivitiesForStudent,student` : `$user->id === $student->id` aussi. Le legacy passait
 *   par `POST v2/activities/todo` (`activityTodoAsStudent`, `can:create_own_challenges`) ; les deux
 *   routes mènent à `V2\ActivityController::newTodoActivity`, qui retombe sur `$request->user()`.
 *   Seule différence : le réducteur de `createActivityAsTutor` ne range rien dans le compte de
 *   l'élève, donc la liste est relue après une création (`onCreated`) ;
 * - `generateMaterialsSkillsPart{1,2,3}` → `POST v2/materials/generate`, `can:generate_materials`,
 *   que le rôle `student` a (`RolesAndPermissionsSeeder`).
 * Une seule route reste fermée à l'élève : les compétences d'un chapitre
 * (`can:manage_student_chapters`). Le formulaire ne les montre pas (`STUDENT_GENERATION_FIELDS`) ;
 * le crochet partagé les demande quand même au choix d'un chapitre, et reçoit un 403 sans effet.
 *
 * Ce que l'élève a en plus du parent : ses contrôles à venir (`getSchoolAssessments`, à
 * l'ouverture, `GenerateActivityModal.js:644-653`) pour remonter les chapitres du prochain ; un
 * chapitre OU des instructions pour avancer ; la recherche sans résultat qui bascule vers
 * Instructions.
 */
import { useCallback, useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"

import { apiRequest } from "../../../../../../data/redux/actions/api"
import { schoolAssessmentsService } from "../../../../../../data/services/schoolAssessmentsService"
import { type GenerateActivityFlowProps } from "../../../../../../design-system/templates/GenerateActivityFlow"
import {
    describeInInstructions,
    missingStudentGenerationField,
    nextAssessmentForSubject,
    STUDENT_GENERATION_FIELDS,
    type ApiStudentSchoolAssessment,
} from "../../../../adapters/student/Activities/studentGenerationAdapter"
import { studentRefusalText } from "../../../hooks/studentRefusal"
import { useGenerateActivityFlow } from "../../../hooks/useGenerateActivityFlow"
import { type UIActivityGenerationDraft } from "../../../types"

/** Le repli d'une création refusée, au tutoiement. */
const STUDENT_CREATE_FAILED = "Impossible de créer cette activité. Réessaie dans un instant."

export type UseStudentGenerateActivityArgs = {
    /** L'élève connecté — son identifiant part dans l'URL des chapitres et de la création. */
    studentId: string | number | null
    /** Rejoué après une création : la liste doit montrer la nouvelle activité. */
    onCreated?: () => void
}

export function useStudentGenerateActivity(args: UseStudentGenerateActivityArgs) {
    const { studentId, onCreated } = args
    const dispatch = useDispatch()
    const flow = useGenerateActivityFlow({ studentId, onCreated })
    const base = flow.generate

    // Les contrôles à venir, demandés à l'ouverture comme le legacy ; le réducteur
    // `schoolAssessments` les range par élève, l'accueil lit la même entrée.
    useEffect(() => {
        if (!base.visible || studentId == null) return
        dispatch(
            apiRequest(
                schoolAssessmentsService.getSchoolAssessments,
                {},
                {},
                { student_id: studentId, upcoming: true }
            ) as never
        )
    }, [dispatch, base.visible, studentId])

    const assessments = useSelector(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (state: any) =>
            (studentId != null ? state?.data?.schoolAssessments?.byStudentId?.[studentId] : undefined) as
                ApiStudentSchoolAssessment[] | undefined
    )
    const subjectId = base.draft.subjectId
    const nextAssessment = useMemo(() => nextAssessmentForSubject(assessments, subjectId), [assessments, subjectId])

    // Instructions devient le chemin recommandé après une recherche sans issue ; choisir un
    // chapitre l'éteint pour de bon (`MaterialGenerationForm.js:137-145`).
    const [instructionsRecommended, setInstructionsRecommended] = useState(false)
    const baseDraftChange = base.onDraftChange
    const onDraftChange = useCallback(
        (draft: UIActivityGenerationDraft) => {
            if (draft.chapterId != null) setInstructionsRecommended(false)
            baseDraftChange(draft)
        },
        [baseDraftChange]
    )
    const draft = base.draft
    const onDescribeInInstructions = useCallback(
        (searchText: string) => {
            setInstructionsRecommended(true)
            const next = describeInInstructions(draft, searchText)
            if (next !== draft) baseDraftChange(next)
        },
        [baseDraftChange, draft]
    )

    const generate: GenerateActivityFlowProps = {
        ...base,
        onDraftChange,
        voice: "student",
        allowFile: false,
        formFields: STUDENT_GENERATION_FIELDS,
        nextAssessment,
        chapterOrInstructions: true,
        onDescribeInInstructions,
        instructionsRecommended: instructionsRecommended && draft.chapterId == null,
        // La règle du formulaire est la sienne : un chapitre OU des instructions.
        missingField: missingStudentGenerationField(draft),
        // Le refus d'une création, dit à l'élève (le parent garde la phrase du flux).
        error: base.error != null ? studentRefusalText(base.error, STUDENT_CREATE_FAILED) : base.error,
    }

    return { open: flow.open, generate }
}
